import React, { createContext, useContext, useEffect, useReducer } from "react";
import {
  AuthState,
  //   User,
  //   UserProfile,
  //   UserGameData,
  //   Inventory,
  LoginCredentials,
  RegisterData,
} from "../types/auth";
import { useAuth } from "../hooks/useAuth";

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  registerSuccess: boolean;
  clearRegisterSuccess: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Reducer para manejar el estado de autenticación
const authReducer = (state: AuthState, action: any): AuthState => {
  switch (action.type) {
    case "REGISTER_SUCCESS":
      return {
        ...state,
        isLoading: false,
        registerSuccess: true,
        error: null,
      };
    case "CLEAR_REGISTER_SUCCESS":
      return {
        ...state,
        registerSuccess: false,
      };
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload, isLoading: false };
    case "LOGIN_SUCCESS":
      return {
        ...state,
        user: action.payload.user,
        profile: action.payload.profile,
        gameData: action.payload.gameData,
        inventory: action.payload.inventory,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case "LOGOUT":
      return {
        user: null,
        profile: null,
        gameData: null,
        inventory: null,
        isAuthenticated: false,
        registerSuccess: false,
        isLoading: false,
        error: null,
      };
    case "CLEAR_ERROR":
      return { ...state, error: null };
    default:
      return state;
  }
};

const initialState: AuthState = {
  user: null,
  profile: null,
  gameData: null,
  inventory: null,
  isAuthenticated: false,
  isLoading: true,
  registerSuccess: false,
  error: null,
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const { checkExistingSession, performLogin, performRegister, performLogout } =
    useAuth();

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        dispatch({ type: "SET_LOADING", payload: true });
        const sessionData = await checkExistingSession();

        if (sessionData) {
          dispatch({
            type: "LOGIN_SUCCESS",
            payload: sessionData,
          });
        } else {
          dispatch({ type: "SET_LOADING", payload: false });
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
        dispatch({ type: "SET_LOADING", payload: false });
      }
    };

    initializeAuth();
  }, [checkExistingSession]);

  const login = async (credentials: LoginCredentials) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      const userData = await performLogin(credentials);
      dispatch({
        type: "LOGIN_SUCCESS",
        payload: userData,
      });
    } catch (error: any) {
      dispatch({ type: "SET_ERROR", payload: error.message });
      throw error;
    }
  };

  const register = async (data: RegisterData) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      await performRegister(data);
      // Después del registro, hacer login automáticamente
      dispatch({ type: "REGISTER_SUCCESS" });
    } catch (error: any) {
      dispatch({ type: "SET_ERROR", payload: error.message });
      throw error;
    }
  };

  const clearRegisterSuccess = () => {
    dispatch({ type: "CLEAR_REGISTER_SUCCESS" });
  };

  const logout = () => {
    performLogout();
    dispatch({ type: "LOGOUT" });
  };

  const clearError = () => {
    dispatch({ type: "CLEAR_ERROR" });
  };

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    clearError,
    registerSuccess: state.registerSuccess,
    clearRegisterSuccess,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};
